import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, BookOpen, Lock, X, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isMember } from "@/lib/access";

const TAGLINES = [
  "Join the network.",
  "Find your tribe.",
  "Where your tribe finds you.",
  "Where the tribe gets recognized.",
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] } }),
};

function StoryReader({ story, onClose }) {
  const [gated, setGated] = useState(false);
  const [tag, setTag] = useState(0);
  const [full, setFull] = useState(story);

  // Pull the article body so the teaser shows real prose before locking.
  useEffect(() => {
    let alive = true;
    api.get(`/articles/${story.slug}`).then(({ data }) => { if (alive) setFull({ ...story, ...data }); }).catch(() => {});
    return () => { alive = false; };
  }, [story]);

  // Reveal the conversion gate after the 2-second preview window.
  useEffect(() => {
    const t = setTimeout(() => setGated(true), 2000);
    return () => clearTimeout(t);
  }, []);

  // Rotate the brand taglines while the gate is showing.
  useEffect(() => {
    if (!gated) return;
    const i = setInterval(() => setTag((p) => (p + 1) % TAGLINES.length), 2200);
    return () => clearInterval(i);
  }, [gated]);

  const preview = full.body || `${full.excerpt}\n\n${full.excerpt}`;

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="story-reader-modal"
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ scale: 0.96, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-2xl max-h-[88vh] overflow-hidden bg-[#0c0c0c] border border-white/10 shadow-2xl"
      >
        <button onClick={onClose} data-testid="story-reader-close"
          className="absolute top-3 right-3 z-30 h-9 w-9 flex items-center justify-center bg-black/60 border border-white/15 text-neutral-300 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors">
          <X size={16} />
        </button>

        <div className="relative h-52 sm:h-64 overflow-hidden">
          <img src={full.cover} alt={full.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/30 to-transparent" />
          <div className="absolute bottom-4 left-5 right-5">
            <span className="text-xs font-mono uppercase tracking-widest text-[#D4AF37]">{full.category}</span>
            <h3 className="mt-2 font-display text-2xl sm:text-3xl font-bold leading-tight">{full.title}</h3>
          </div>
        </div>

        <div className="relative">
          <div className="px-5 sm:px-8 py-6 min-h-[320px] max-h-[42vh] overflow-hidden text-neutral-300 leading-relaxed whitespace-pre-line">
            {preview}
          </div>

          <AnimatePresence>
            {gated && (
              <motion.div
                initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 py-8 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/95 to-[#0c0c0c]/40 backdrop-blur-[3px]"
                data-testid="story-gate"
              >
                <Lock className="text-[#D4AF37] mb-4" size={26} />
                <div className="h-14 flex items-center justify-center overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={tag}
                      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}
                      transition={{ duration: 0.45 }}
                      className="font-display text-2xl sm:text-3xl font-bold gold-text"
                      data-testid="story-gate-tagline"
                    >
                      {TAGLINES[tag]}
                    </motion.p>
                  </AnimatePresence>
                </div>
                <p className="mt-3 text-sm text-neutral-400 max-w-sm">
                  The full story lives inside Barbistry. Join the quality barber network to keep reading and get recognized.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <Link to="/register" className="w-full sm:w-auto">
                    <Button className="w-full rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-11 px-8 group" data-testid="story-gate-join">
                      Join the network <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                  <Link to="/directory" className="w-full sm:w-auto">
                    <Button variant="outline" className="w-full rounded-none border-white/25 bg-transparent text-white hover:border-[#D4AF37] hover:text-[#D4AF37] h-11 px-8" data-testid="story-gate-tribe">
                      Find your tribe
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function StoryTeaser() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const member = isMember(user);
  const scrollLock = useRef(false);

  useEffect(() => {
    api.get("/articles").then(({ data }) => {
      const week = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
      const rot = data.length ? week % data.length : 0;
      const rotated = [...data.slice(rot), ...data.slice(0, rot)];
      setItems(rotated.slice(0, 4));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (active && !scrollLock.current) { document.body.style.overflow = "hidden"; scrollLock.current = true; }
    if (!active && scrollLock.current) { document.body.style.overflow = ""; scrollLock.current = false; }
    return () => { document.body.style.overflow = ""; };
  }, [active]);

  const openStory = (a) => {
    // Members read the full article; everyone else gets the 2-second teaser + gate.
    if (member || user?.role === "admin") { navigate(`/article/${a.slug}`); return; }
    setActive(a);
  };

  if (items.length === 0) return null;

  return (
    <section className="relative z-10 border-b border-white/10 bg-[#080808]" data-testid="story-teaser">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-4 flex items-center gap-2"><BookOpen size={14} /> The Barber Journal</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold">Stories from the chair.</h2>
            <p className="mt-3 text-neutral-400 max-w-lg">Real barbers, real craft, real growth. Get a taste — the rest is for the tribe.</p>
          </div>
          <Link to="/news"><Button variant="outline" className="rounded-none border-white/25 bg-transparent text-white hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="story-all-news">All stories <ArrowRight size={15} className="ml-2" /></Button></Link>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((a, i) => (
            <motion.button
              key={a.id} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
              onClick={() => openStory(a)} data-testid={`story-card-${a.slug}`}
              className="group text-left bg-[#111] border border-white/10 overflow-hidden hover:-translate-y-1 hover:border-[#D4AF37]/40 transition-transform duration-300"
            >
              <div className="relative h-44 overflow-hidden">
                <img src={a.cover} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <span className="absolute top-3 left-3 text-xs font-mono uppercase bg-black/70 border border-white/10 px-2 py-1 text-neutral-200">{a.category}</span>
              </div>
              <div className="p-5">
                <h3 className="font-display text-lg font-semibold leading-snug line-clamp-2">{a.title}</h3>
                <p className="mt-2 text-sm text-neutral-400 line-clamp-2">{a.excerpt}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs text-[#D4AF37]">Read the story <ArrowRight size={13} /></span>
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 border border-[#D4AF37]/30 bg-[#111] p-6">
          <p className="text-neutral-200 font-display text-lg flex items-center gap-2"><Users size={18} className="text-[#D4AF37]" /> This is where your tribe finds you — and where it gets recognized.</p>
          <Link to="/register"><Button className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-11 px-8 whitespace-nowrap" data-testid="story-join">Join Barbistry</Button></Link>
        </div>
      </div>

      <AnimatePresence>
        {active && <StoryReader story={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
